import Color from 'color';
import type { DeepPartial } from 'ts-essentials';

import { convertToDarkRgba } from '@hivebrite/design-system/Theme';
import type { Colors, Theme } from '@hivebrite/design-system/Theme';

import type { NetworkTheming } from 'hb-react/types/network';

export const convertRgbToRgbA = (opacity: number | string, color?: string) => {
  if (!color) {
    return '';
  }

  const rgbColor = Color(color).rgb();
  const initialAlpha = rgbColor.alpha();

  const finalAlpha =
    typeof opacity === 'string' ? parseInt(opacity, 10) : opacity;

  return rgbColor.alpha(finalAlpha || initialAlpha).string();
};

export const getNetworkColor = (
  initialColor?: { color: string; opacity: number | string } | string,
  colors?: Colors,
): string | undefined => {
  if (typeof initialColor === 'object' && colors) {
    const { color, opacity = 1 } = initialColor;

    return convertRgbToRgbA(opacity, colors[color]);
  }

  if (typeof initialColor === 'string') {
    return initialColor;
  }

  return undefined;
};

export const getBackgroundColor = ({
  networkTheming,
}: {
  networkTheming?: DeepPartial<NetworkTheming>;
}) =>
  networkTheming?.fo?.fo_background_type === 'SOLID'
    ? networkTheming?.fo?.fo_background_color
    : networkTheming?.fo?.fo_background_gradient?.inlineCSS;

export const getBrandColors = ({ theme }: { theme: Theme }) => [
  {
    label: __('Primary'),
    value: Color(theme.colors.primary).hex().toLowerCase(),
  },
  {
    label: __('Header'),
    value: Color(theme.colors.header).hex().toLowerCase(),
  },
  {
    label: __('Link'),
    value: Color(theme.colors.link).hex().toLowerCase(),
  },
];

export const formatColor = ({ color }: { color: string }) =>
  Color(color).hex().toLowerCase();

export const getColorPresetsArray = ({
  colors = [],
  theme,
  length,
}: {
  colors: Array<string>;
  theme: Theme;
  length: number;
}) =>
  new Array(length)
    .fill(undefined)
    .map((_, index) =>
      colors[index]
        ? Color(colors[index]).hex().toLowerCase()
        : Color(theme.colors.white).hex().toLowerCase(),
    );

export const getButtonsHoveredBackgroundColor = ({
  variant,
  theme,
  backgroundColor,
}: {
  // TODO: does not accept ButtonVariant as we try to access `hovered`.
  variant: any;
  theme: Theme;
  backgroundColor?: string | Color;
}) => {
  const isBgColorAThemeBgButtonColor =
    backgroundColor === theme.colors[theme.buttons?.[variant]?.bg];

  return isBgColorAThemeBgButtonColor
    ? theme.buttons[variant].hovered.bg
    : convertToDarkRgba(backgroundColor);
};

export const getButtonsHoveredTextColor = ({
  variant,
  theme,
  backgroundColor,
  color,
}: {
  // TODO: does not accept ButtonVariant as we try to access `hovered`.
  variant: any;
  theme: Theme;
  backgroundColor?: string | Color;
  color?: string | Color;
}) => {
  const isBgColorAThemeBgButtonColor =
    backgroundColor === theme.colors[theme.buttons?.[variant]?.bg];

  return isBgColorAThemeBgButtonColor
    ? // hovered.color does not exist on every theme, fallback on button variant initial text color
      theme.buttons[variant]?.hovered?.color || theme.buttons[variant]?.color
    : color;
};

export const isColorFullyTransparent = ({ color }: { color: string | Color }) =>
  Color(color).alpha() === 0;
